import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../App";
import { toast } from "sonner";
import {
  Building2, ArrowLeft, FileText, Clock, CheckCircle, X,
  DollarSign, ChevronRight, Sparkles, Leaf, Waves, Wind, Wrench, AlertCircle
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  cat_diarista: Sparkles,
  cat_jardinagem: Leaf,
  cat_piscina: Waves,
  cat_arcondicionado: Wind,
  cat_reforma: Wrench
};

const STATUS_CONFIG = {
  pending: { label: "Aguardando", variant: "secondary", color: "text-amber-600 bg-amber-100" },
  accepted: { label: "Aceito", variant: "default", color: "text-emerald-600 bg-emerald-100" },
  rejected: { label: "Recusado", variant: "destructive", color: "text-red-600 bg-red-100" },
  counter_offered: { label: "Contra-proposta", variant: "outline", color: "text-blue-600 bg-blue-100" },
  expired: { label: "Expirado", variant: "secondary", color: "text-slate-600 bg-slate-100" }
};

export default function BudgetsPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBudgets = async () => {
      try {
        const response = await fetch(`${API_URL}/api/budgets`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setBudgets(data);
        }
      } catch (error) {
        console.error("Error fetching budgets:", error);
        toast.error("Erro ao carregar orçamentos");
      } finally {
        setLoading(false);
      }
    };

    fetchBudgets();
  }, [token]);

  const acceptBudget = async (budgetId) => {
    try {
      const response = await fetch(`${API_URL}/api/budgets/${budgetId}/accept`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const negotiation = await response.json();
        toast.success("Orçamento aceito! Iniciando negociação...");
        navigate(`/negotiations/${negotiation.negotiation_id}`);
      } else {
        throw new Error("Erro ao aceitar orçamento");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="budgets-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate(user?.role === "provider" ? "/provider" : "/client")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <Link to="/" className="flex items-center gap-2">
                <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
              </Link>
              <span className="font-semibold text-slate-900">Orçamentos</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            {user?.role === "client" ? "Orçamentos Recebidos" : "Meus Orçamentos"}
          </h1>
          <p className="text-slate-600">
            {budgets.length} orçamento{budgets.length !== 1 ? "s" : ""}
          </p>
        </div>

        {budgets.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                Nenhum orçamento
              </h3>
              <p className="text-slate-500 mb-4">
                {user?.role === "client" 
                  ? "Crie uma solicitação para receber orçamentos de prestadores."
                  : "Envie orçamentos para as solicitações disponíveis."
                }
              </p>
              <Button onClick={() => navigate(user?.role === "client" ? "/discovery" : "/provider")}>
                {user?.role === "client" ? "Criar Solicitação" : "Ver Solicitações"}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {budgets.map((budget) => {
              const Icon = CATEGORY_ICONS[budget.category_id] || FileText;
              const status = STATUS_CONFIG[budget.status] || STATUS_CONFIG.pending;

              return (
                <Card key={budget.budget_id} className="overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      {/* Budget Info */}
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon className="w-6 h-6 text-slate-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-slate-900">
                              Orçamento #{budget.budget_id.slice(-8)}
                            </h3>
                            <Badge className={status.color}>{status.label}</Badge>
                          </div>
                          <p className="text-sm text-slate-500 mb-2">
                            Criado em {new Date(budget.created_at).toLocaleDateString("pt-BR")}
                          </p>
                          {budget.notes && (
                            <p className="text-sm text-slate-600 line-clamp-2">{budget.notes}</p>
                          )}
                        </div>
                      </div>

                      {/* Price */}
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Valor Total</p>
                        <p className="text-2xl font-bold text-slate-900">
                          R$ {budget.total?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                        </p>
                        <div className="text-xs text-slate-500 mt-1">
                          <span>Mão de obra: R$ {budget.labor_cost?.toLocaleString("pt-BR")}</span>
                          {budget.materials_cost > 0 && (
                            <span> • Materiais: R$ {budget.materials_cost?.toLocaleString("pt-BR")}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Items */}
                    {budget.items?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <p className="text-sm font-medium text-slate-700 mb-2">Itens incluídos:</p>
                        <div className="flex flex-wrap gap-2">
                          {budget.items.slice(0, 5).map((item, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {item.name || item.description || `Item ${idx + 1}`}
                            </Badge>
                          ))}
                          {budget.items.length > 5 && (
                            <Badge variant="outline" className="text-xs">
                              +{budget.items.length - 5} mais
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    {user?.role === "client" && budget.status === "pending" && (
                      <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-3">
                        <Button 
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => acceptBudget(budget.budget_id)}
                          data-testid={`accept-budget-${budget.budget_id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Aceitar e Negociar
                        </Button>
                        <Button variant="outline">
                          <X className="w-4 h-4 mr-2" />
                          Recusar
                        </Button>
                      </div>
                    )}

                    {budget.status === "accepted" && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <Button 
                          variant="outline"
                          onClick={() => navigate(`/negotiations/${budget.budget_id}`)}
                        >
                          Ver Negociação
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
